import {
    ADMINISTRATIVE_ROLES,
    BusinessServices,
    Country,
    IRole,
    IUser,
    Roles
} from '@shs-digi-care-monorepo/interfaces';

/**
 * Country- and stream-scoped roles of a user. Empty for users that are still served with
 * the deprecated flat `roles` list only.
 */
export function scopedRoles(user: IUser | undefined | null): IRole[] {
    return (user?.userRoles ?? []).map(({ role }) => role);
}

/**
 * Business services a user holds a role for. Every role is scoped to a stream, so each
 * assignment contributes one.
 */
export function assignedStreams(user: IUser | undefined | null): BusinessServices[] {
    const streams = scopedRoles(user).map((role) => role.stream);

    return [...new Set(streams)];
}

/**
 * Names of the roles a user holds. `IUser.roles` is deprecated in favour of the scoped
 * `IUser.userRoles` and is only read while the API still serves users without them.
 */
export function roleNames(user: IUser | undefined | null): Roles[] {
    return scopedRoles(user).map(({ name }) => name);
}

/**
 * Names of the roles a user holds within the given stream.
 */
export function roleNamesForStream(user: IUser | undefined | null, stream: BusinessServices): Roles[] {
    return scopedRoles(user)
        .filter((role) => role.stream === stream)
        .map(({ name }) => name);
}

export function hasRole(user: IUser | undefined | null, role: Roles): boolean {
    return roleNames(user).includes(role);
}
export function isAdmin(user: IUser | undefined | null): boolean {
    return hasRole(user, Roles.ADMIN);
}

export function isTaskOwner(user: IUser | undefined | null): boolean {
    return roleNames(user).some((role) => [Roles.ADMIN, Roles.TASK_OWNER].includes(role));
}

export function isAssignee(user: IUser | undefined | null, request: { assignee?: string | null }): boolean {
    return !!user?.gid && request.assignee === user.gid;
}

export function isInternal(user: IUser | undefined | null): boolean {
    return isTaskOwner(user) || !!user?.isInternal;
}

/**
 * Checks if the user has only the 'Requestor' role
 */
export function isRequestor(user: IUser | null | undefined): boolean {
    const roles = roleNames(user);

    return roles.length === 1 && roles.includes(Roles.REQUESTOR);
}

/**
 * Business services the user holds a role in. Every role is scoped to a stream, so each
 * assignment contributes one.
 */
export function streamsOf(user: IUser | undefined | null): BusinessServices[] {
    const streams = scopedRoles(user).map(({ stream }) => stream);

    return [...new Set(streams)];
}

/** `USA` is the deprecated code for `US`, which the seeded roles and the country list use. */
export function normalizeCountry(country: Country): Country {
    return country === Country.USA ? Country.US : country;
}

/**
 * How privileged a role is. Every non-administrative role shares the bottom level, so a requestor
 * and a task owner are equally administered.
 */
export function roleLevel(name: Roles): number {
    return ADMINISTRATIVE_ROLES.indexOf(name) + 1;
}

/**
 * Whether a role covers a scope - a business service together with a country. A country is covered
 * by the roles of that country and by the stream-wide ones, which carry none. The stream-wide
 * scope - the scope of the administrator role itself - is only ever covered by another stream-wide
 * role, which is what keeps a steward away from the administrators of their region.
 */
export function coversScope(role: IRole, stream?: BusinessServices | null, country?: Country | null): boolean {
    if (role.stream !== stream) {
        return false;
    }

    return country ? !role.country || normalizeCountry(role.country) === normalizeCountry(country) : !role.country;
}

/**
 * How privileged the given administrative roles are within a scope: the level of the most
 * privileged one covering it, and zero when none does.
 */
export function administrativeLevel(
    roles: IRole[],
    stream?: BusinessServices | null,
    country?: Country | null
): number {
    return roles
        .filter((role) => coversScope(role, stream, country))
        .reduce((level, role) => Math.max(level, roleLevel(role.name)), 0);
}

/** The administrative roles the user holds within a business service. */
function administrativeRolesIn(user: IUser | undefined | null, stream: BusinessServices): IRole[] {
    return scopedRoles(user).filter((role) => role.stream === stream && ADMINISTRATIVE_ROLES.includes(role.name));
}

/**
 * Whether the user administers anybody within a business service. Working in one - as a requestor
 * or a task owner - is not enough.
 */
export function administersStream(user: IUser | undefined | null, stream: BusinessServices): boolean {
    return administrativeRolesIn(user, stream).length > 0;
}

/**
 * The countries the user administers within a business service. An empty result means they are
 * not narrowed by country at all: either they hold a stream-wide administrative role, or they
 * administer the business service not at all - {@link administersStream} tells the two apart.
 */
export function administeredCountries(user: IUser | undefined | null, stream: BusinessServices): Country[] {
    const roles = administrativeRolesIn(user, stream);
    if (roles.some((role) => !role.country)) {
        return [];
    }

    return [...new Set(roles.map((role) => role.country as Country))];
}

/**
 * Narrows a list of countries down to the ones the user administers within a business service.
 * A stream-wide administrator keeps the whole list, the country-scoped administrative roles keep
 * their own countries and anybody without an administrative role keeps none.
 */
export function narrowToAdministered(
    user: IUser | undefined | null,
    stream: BusinessServices,
    countries: Country[]
): Country[] {
    if (!administersStream(user, stream)) {
        return [];
    }

    const administered = administeredCountries(user, stream);

    return administered.length ? countries.filter((country) => administered.includes(country)) : countries;
}
